import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FileText, Loader2, Printer, Truck } from "lucide-react";
import api from "../lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function BillPage() {
  const { awb } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [phone, setPhone] = useState(params.get("p") || "");
  const [needPhone, setNeedPhone] = useState(false);

  const load = (ph) => {
    api.get(`/public/invoice/${awb}?phone=${encodeURIComponent(ph)}`)
      .then((r) => { setData(r.data); setNeedPhone(false); setErr(""); })
      .catch((e) => {
        if (e.response?.status === 403) { setNeedPhone(true); setErr(""); }
        else setErr(e.response?.data?.detail || "Bill not found for this AWB");
      });
  };

  useEffect(() => { load(phone); /* eslint-disable-next-line */ }, [awb]);

  const download = () => {
    window.open(`${window.location.origin}/api/public/bill/${data.shipment.awb}.pdf?phone=${encodeURIComponent(phone)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bill-page">
      <div className="mx-auto max-w-lg px-4 py-14">
        <div className="mb-8 text-center">
          <p className="font-heading text-2xl font-extrabold tracking-tight text-navy">
            <span className="text-saffron">INDIA</span><span className="text-express">FAST</span>
          </p>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-green-700">Global Couriers</p>
        </div>

        {needPhone && (
          <div className="rounded-2xl border border-amber-200 bg-white p-8 text-center" data-testid="bill-phone-gate">
            <p className="font-bold text-navy">Verify to view this bill</p>
            <p className="mt-1 text-sm text-slate-500">Enter the last 4 digits of the sender or receiver phone number.</p>
            <form onSubmit={(e) => { e.preventDefault(); load(phone); }} className="mt-4 flex gap-2">
              <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(-4))} data-testid="bill-phone-input"
                     className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-center font-mono text-sm outline-none focus:border-saffron"
                     placeholder="Last 4 digits" maxLength={4} />
              <button className="rounded-lg bg-saffron px-4 py-2.5 text-sm font-bold text-white" data-testid="bill-phone-submit">Verify</button>
            </form>
          </div>
        )}

        {err && (
          <div className="rounded-2xl border border-red-200 bg-white p-10 text-center" data-testid="bill-error">
            <FileText className="mx-auto h-10 w-10 text-red-300" />
            <p className="mt-3 font-bold text-navy">Bill not available</p>
            <p className="mt-1 text-sm text-slate-500">{err}</p>
            <Link to={`/track/${awb}`} className="mt-4 inline-block text-sm font-bold text-express hover:underline">Track this shipment instead →</Link>
          </div>
        )}

        {!data && !err && (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-saffron" />
            <p className="mt-3 text-sm text-slate-500">Loading your bill…</p>
          </div>
        )}

        {data && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="bill-card">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Tax Invoice</p>
                <p className="mt-1 font-mono text-sm font-bold text-navy" data-testid="bill-invoice-no">{data.invoice.invoice_number}</p>
              </div>
              <Truck className="h-8 w-8 text-saffron" />
            </div>
            <div className="space-y-3 py-5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">AWB</span><span className="font-mono font-bold" data-testid="bill-awb">{data.shipment.awb}</span></div>
              {data.shipment.customer_name && <div className="flex justify-between"><span className="text-slate-500">Billed To</span><span className="font-semibold">{data.shipment.customer_name}</span></div>}
              {(data.shipment.origin_city || data.shipment.destination_city || data.shipment.destination_country) && (
                <div className="flex justify-between"><span className="text-slate-500">Route</span>
                  <span className="font-semibold">{data.shipment.origin_city || "India"} → {data.shipment.destination_city || data.shipment.destination_country}</span></div>
              )}
              {data.shipment.chargeable_weight > 0 && <div className="flex justify-between"><span className="text-slate-500">Chargeable Weight</span><span className="font-semibold" data-testid="bill-weight">{data.shipment.chargeable_weight} kg</span></div>}
              <div className="flex justify-between border-t border-slate-100 pt-3">
                <span className="font-bold text-navy">Total (incl. 18% GST)</span>
                <span className="font-heading text-xl font-extrabold text-saffron" data-testid="bill-amount">{inr(data.invoice.amount ?? data.shipment.customer_price)}</span>
              </div>
            </div>
            <button onClick={download} data-testid="bill-download-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-saffron py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover">
              <Printer className="h-4 w-4" /> Download Bill (PDF)
            </button>
            <Link to={`/track/${data.shipment.awb}`} data-testid="bill-track-link"
                  className="mt-3 flex items-center justify-center rounded-xl border border-slate-200 py-3 text-sm font-bold text-navy hover:bg-slate-50">
              Track Shipment
            </Link>
          </div>
        )}
        <p className="mt-6 text-center text-[11px] text-slate-400">Thank you for shipping with INDIAFAST · ccare@indiafast.in</p>
      </div>
    </div>
  );
}
